import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { FaPlus, FaLock, FaLockOpen } from "react-icons/fa6";
import mainosKuva from "../../assets/mainoskortti.webp";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";

/** Lisää/poista new & skipUser URL-parameterit hallitusti */
function ensureNewQuery(href, { allowNew = false } = {}) {
  const s = String(href || "/setup");
  const [path, rawSearch = ""] = s.split("?");
  const usp = new URLSearchParams(rawSearch);
  if (allowNew) {
    if (!usp.has("new") && !usp.has("skipUser")) usp.set("new", "1");
  } else {
    usp.delete("new");
    usp.set("skipUser", "1");
  }
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

const AdCard = ({
  ad,
  /** pakota lukkoon */
  locked = false,
  /** backendin totuus: voiko uuden kortin luoda (paikka ostettu/avattu) */
  canAdd = true,
  /** valinnaiset slot-näytöt */
  slotsUsed,
  slotsMax,
  /** minne ohjataan kun lukossa (oston/avauksen sivu) */
  lockedCtaHref = "/otayhteytta",
  /** minne ohjataan jos kortti on lukossa ja media-alue klikattais (säilytetty prop) */
  dashboardHref = "/dashboard?tab=bands",
}) => {
  const isLocked = Boolean(locked || !canAdd);

  const used = Number.isFinite(+slotsUsed) ? +slotsUsed : undefined;
  const max  = Number.isFinite(+slotsMax)  ? +slotsMax  : undefined;

  // Älä ohjaa UKK:iin – käytä setupia pohjana
  const rawHref = ad?.cta_link;
  const hrefBase =
    rawHref && !/\/(ukk|useinkysyttya|faq)$/i.test(String(rawHref))
      ? String(rawHref)
      : "/setup";

  // Lukossa → ei wizardia; Auki → lisää new=1
  const url = isLocked ? dashboardHref : ensureNewQuery(hrefBase, { allowNew: true });

  const state = isLocked ? undefined : { ...(ad?.cta_state || {}), new: true, skipUser: true };

  const title = ad?.ad_title || "Lisää esiintyjäkortti";

  // Badge + ikonit
  const badge = isLocked
    ? { label: "Maksullinen", Icon: FaLock, bg: "from-sky-500 to-cyan-600" }
    : { label: "Ostettu",     Icon: FaLockOpen, bg: "from-green-500 to-emerald-600" };

  // Selite: ei lupausta ennakkoluonnista
  const subtitle = isLocked
    ? (Number.isFinite(max) && Number.isFinite(used)
        ? `Lisäpaikka on maksullinen. Paikat käytössä ${used} / ${max}. Avaa/osta paikka, jotta voit luoda kortin.`
        : "Lisäpaikka on maksullinen. Avaa/osta paikka, jotta voit luoda kortin.")
    : "Lisäpaikka on ostettu – voit luoda kortin nyt.";

  const ctaText = isLocked ? "Avaa lisäpaikka" : (ad?.cta_text || "Luo kortti");
  const bg = ad?.profile_image || mainosKuva;

  const onStartNewCard = () => { try { sessionStorage.setItem("setup.newCard", "1"); } catch {} };

  return (
    <div
      className={[
        "group relative overflow-hidden rounded-2xl border border-white/10 dark:border-white/5",
        "bg-white/60 dark:bg-black/50 backdrop-blur shadow-lg",
        "transition-transform duration-300",
        !isLocked && "hover:shadow-neon hover:scale-[1.03]",
      ].filter(Boolean).join(" ")}
      aria-label="Lisää uusi esiintyjäkortti"
      aria-disabled={isLocked}
      tabIndex={isLocked ? -1 : 0}
    >
      {/* sheen + neon ring */}
      <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/5 dark:ring-white/10 transition-shadow duration-500 group-hover:shadow-[0_0_24px_rgba(49,255,217,0.35),0_0_48px_rgba(122,0,255,0.25)]" />
      <span className="pointer-events-none absolute -left-1/3 top-0 h-full w-2/3 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-[-120%] group-hover:translate-x-[180%] transition-transform duration-[1200ms] ease-out rounded-2xl" />

      {/* Media-alue */}
      {isLocked ? (
        <div className="relative block w-full h-40 rounded-t-2xl overflow-hidden">
          <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/30" />
          <div className={`absolute top-2 right-2 z-10 px-2 py-1 rounded-full text-white text-xs font-semibold tracking-wide bg-gradient-to-r ${badge.bg} border border-white/20 shadow-neon-primary`}>
            <span className="inline-flex items-center gap-1"><badge.Icon /> {badge.label}</span>
          </div>
          <div className="absolute inset-0 grid place-items-center">
            <span className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-white/70 bg-black/35 backdrop-blur shadow-lg">
              <FaPlus className="text-white text-3xl md:text-4xl" />
            </span>
          </div>
        </div>
      ) : (
        <Link
          to={url}
          state={state}
          onClick={onStartNewCard}
          className="relative block w-full h-40 rounded-t-2xl overflow-hidden"
        >
          <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/30" />
          <div className={`absolute top-2 right-2 z-10 px-2 py-1 rounded-full text-white text-xs font-semibold tracking-wide bg-gradient-to-r ${badge.bg} border border-white/20 shadow-neon-primary`}>
            <span className="inline-flex items-center gap-1"><badge.Icon /> {badge.label}</span>
          </div>
          <div className="absolute inset-0 grid place-items-center">
            <span className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-white/70 bg-black/35 backdrop-blur transition-transform duration-300 ease-out group-hover:scale-110 shadow-lg">
              <FaPlus className="text-white text-3xl md:text-4xl" />
            </span>
          </div>
        </Link>
      )}

      {/* Tekstit + CTA */}
      <div className="p-3 text-center">
        <h3 className="font-customFont2 text-sm md:text-base text-black/85 dark:text-white/90">{title}</h3>
        <p className="mt-1 text-[11px] md:text-xs text-black/70 dark:text-white/70">{subtitle}</p>

        {/* valinnainen slot-pilleri */}
        {Number.isFinite(max) && Number.isFinite(used) && (
          <div className="mt-2 inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-xs font-semibold
                          bg-white/70 dark:bg-white/10 ring-1 ring-black/10 dark:ring-white/15">
            {used} / {max} korttipaikkaa käytössä
          </div>
        )}

        <div className="mt-3">
          {isLocked ? (
            <NeonButton
              as={Link}
              to={lockedCtaHref}
              variant="primary"
              size="sm"
              className="rounded-xl"
            >
              {ctaText}
            </NeonButton>
          ) : (
            <NeonButton
              as={Link}
              to={url}
              state={state}
              onClick={onStartNewCard}
              variant="primary"
              size="sm"
              className="rounded-xl"
            >
              {ctaText}
            </NeonButton>
          )}
        </div>
      </div>

      {/* Tumma overlay kun lukittu – ei estä napin klikkausta */}
      {isLocked && (
        <div className="pointer-events-none absolute inset-0 z-30 rounded-2xl bg-black/55" aria-hidden="true" />
      )}
    </div>
  );
};

AdCard.propTypes = {
  ad: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    profile_image: PropTypes.string,
    ad_title: PropTypes.string,
    description: PropTypes.string,
    bullets: PropTypes.string,
    cta_text: PropTypes.string,
    cta_link: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    name: PropTypes.string,
    cta_state: PropTypes.object,
  }),
  locked: PropTypes.bool,
  canAdd: PropTypes.bool,
  slotsUsed: PropTypes.number,
  slotsMax: PropTypes.number,
  lockedCtaHref: PropTypes.string,
  dashboardHref: PropTypes.string,
};

export default AdCard;
